import React, { useState, useEffect, Suspense, lazy } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Users, DollarSign, FileText, TrendingUp, Loader2, ShieldAlert, Percent, Mail, Send, CheckCircle2, RotateCcw, Megaphone, BookOpen, Ticket, AlertTriangle, AlertOctagon, MousePointerClick, Radio, Ban } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { AmbassadorsTab } from '../components/AdminAmbassadors';

// AdminBlog carries react-markdown + remark-gfm — lazy-load so those libs
// only ship when an admin opens the Blog tab.
const BlogTab = lazy(() => import('../components/AdminBlog').then((m) => ({ default: m.BlogTab })));
const CompCodesTab = lazy(() => import('../components/AdminCompCodes').then((m) => ({ default: m.CompCodesTab })));
const EmailPreviewTab = lazy(() => import('../components/AdminEmailPreview').then((m) => ({ default: m.AdminEmailPreview })));
const LinkTapsTab = lazy(() => import('../components/AdminLinkTaps').then((m) => ({ default: m.LinkTapsTab })));
const StuckPaymentsTab = lazy(() => import('../components/AdminStuckPayments').then((m) => ({ default: m.StuckPaymentsTab })));
const AnnouncementsTab = lazy(() => import('../components/AdminAnnouncements').then((m) => ({ default: m.AnnouncementsTab })));
const AccountsTab = lazy(() => import('../components/AdminAccounts').then((m) => ({ default: m.AccountsTab })));

const StatCard = ({ icon: Icon, label, value, sub, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="pt-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="text-2xl font-bold font-mono">{value}</p>
          {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
        </div>
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <Icon className="w-5 h-5 text-primary" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const DayChart = ({ title, data, testId }) => (
  <Card data-testid={testId}>
    <CardHeader className="pb-2">
      <CardTitle className="text-base">{title}</CardTitle>
    </CardHeader>
    <CardContent>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} interval={4} />
          <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={30} />
          <Tooltip />
          <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </CardContent>
  </Card>
);

const STATUS_COLORS = {
  open: 'bg-chart-3/15 text-foreground',
  replied: 'bg-chart-2/15 text-foreground',
  closed: 'bg-muted text-muted-foreground',
};

const InboxMessage = ({ msg, api, onChanged }) => {
  const [expanded, setExpanded] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.post(`/admin/inbox/${msg.message_id}/reply`, { body: reply });
      toast.success(`Reply sent to ${msg.email}`);
      setReply('');
      onChanged();
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const setStatus = async (status) => {
    try {
      await api.put(`/admin/inbox/${msg.message_id}/status`, { status });
      onChanged();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  return (
    <Card data-testid={`inbox-message-${msg.message_id}`}>
      <CardContent className="pt-4 pb-4">
        <button className="w-full text-left" onClick={() => setExpanded(!expanded)} data-testid={`inbox-expand-${msg.message_id}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-medium text-sm truncate">{msg.subject}</p>
              <p className="text-xs text-muted-foreground truncate">{msg.name} · {msg.email} · {msg.created_at?.slice(0, 10)}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge variant="secondary" className="text-[10px]">{msg.source === 'chat_escalation' ? 'Chat' : 'Contact'}</Badge>
              <Badge className={`text-[10px] ${STATUS_COLORS[msg.status] || ''}`} variant="secondary">{msg.status}</Badge>
            </div>
          </div>
        </button>
        {expanded && (
          <div className="mt-4 space-y-4">
            <p className="text-sm whitespace-pre-wrap bg-muted/40 rounded-md p-3">{msg.message}</p>
            {(msg.replies || []).map((r, i) => (
              <div key={`${msg.message_id}-reply-${i}`} className="text-sm whitespace-pre-wrap border-l-2 border-primary pl-3 text-muted-foreground">
                <p className="text-[10px] uppercase tracking-wide mb-1">You replied · {r.sent_at?.slice(0, 10)}</p>
                {r.body}
              </div>
            ))}
            <div className="space-y-2">
              <Textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder={`Reply to ${msg.name}...`}
                className="min-h-[90px]"
                data-testid={`inbox-reply-input-${msg.message_id}`}
              />
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={sendReply} disabled={sending || !reply.trim()} className="gap-1.5" data-testid={`inbox-reply-send-${msg.message_id}`}>
                  {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  Send Reply
                </Button>
                {msg.status !== 'closed' ? (
                  <Button size="sm" variant="outline" onClick={() => setStatus('closed')} className="gap-1.5" data-testid={`inbox-close-${msg.message_id}`}>
                    <CheckCircle2 className="w-3.5 h-3.5" /> Close
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => setStatus('open')} className="gap-1.5" data-testid={`inbox-reopen-${msg.message_id}`}>
                    <RotateCcw className="w-3.5 h-3.5" /> Reopen
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const InboxTab = ({ api }) => {
  const [data, setData] = useState({ messages: [], counts: {} });
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const fetchInbox = async () => {
    try {
      const params = filter === 'all' ? {} : { status: filter };
      const response = await api.get('/admin/inbox', { params });
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load inbox');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInbox();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4" data-testid="admin-inbox">
      <div className="flex gap-2 flex-wrap">
        {['all', 'open', 'replied', 'closed'].map((f) => (
          <Button key={f} size="sm" variant={filter === f ? 'default' : 'outline'} onClick={() => setFilter(f)} data-testid={`inbox-filter-${f}`}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
            {f !== 'all' && data.counts[f] !== undefined && ` (${data.counts[f]})`}
          </Button>
        ))}
      </div>
      {data.messages.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground text-sm" data-testid="inbox-empty">No messages{filter !== 'all' ? ` with status "${filter}"` : ' yet'}.</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {data.messages.map((msg) => (
            <InboxMessage key={msg.message_id} msg={msg} api={api} onChanged={fetchInbox} />
          ))}
        </div>
      )}
    </div>
  );
};

export default function AdminMetrics() {
  const { api } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    fetchMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMetrics = async () => {
    try {
      const response = await api.get('/admin/metrics');
      setMetrics(response.data);
    } catch (error) {
      if (error.response?.status === 403) setDenied(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (denied || !metrics) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-64 text-center" data-testid="admin-access-denied">
          <ShieldAlert className="w-12 h-12 text-muted-foreground mb-4" />
          <h2 className="text-lg font-semibold">Admin access required</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Your account email is not in the ADMIN_EMAILS list.
          </p>
        </div>
      </Layout>
    );
  }

  const { users, revenue, invoices, payments, campaigns, client_errors, stuck_payments } = metrics;
  const errors24h = client_errors?.last_24h ?? 0;
  const errorsTotal = client_errors?.total ?? 0;
  const stuckCount = stuck_payments?.count_24h ?? 0;

  return (
    <Layout>
      <div className="p-6 lg:p-12 max-w-7xl mx-auto space-y-8" data-testid="admin-metrics-page">
        <div>
          <h1 className="text-3xl font-heading font-bold">Admin</h1>
          <p className="text-muted-foreground">Launch traction at a glance · updated {new Date(metrics.generated_at).toLocaleString()}</p>
        </div>

        <Tabs defaultValue="metrics">
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="metrics" className="gap-1.5" data-testid="admin-tab-metrics">
              <TrendingUp className="w-4 h-4" /> Metrics
            </TabsTrigger>
            <TabsTrigger value="inbox" className="gap-1.5" data-testid="admin-tab-inbox">
              <Mail className="w-4 h-4" /> Inbox
            </TabsTrigger>
            <TabsTrigger value="ambassadors" className="gap-1.5" data-testid="admin-tab-ambassadors">
              <Megaphone className="w-4 h-4" /> Ambassadors
            </TabsTrigger>
            <TabsTrigger value="comp-codes" className="gap-1.5" data-testid="admin-tab-comp-codes">
              <Ticket className="w-4 h-4" /> Comp Codes
            </TabsTrigger>
            <TabsTrigger value="blog" className="gap-1.5" data-testid="admin-tab-blog">
              <BookOpen className="w-4 h-4" /> Blog
            </TabsTrigger>
            <TabsTrigger value="emails" className="gap-1.5" data-testid="admin-tab-emails">
              <Mail className="w-4 h-4" /> Emails
            </TabsTrigger>
            <TabsTrigger value="link-taps" className="gap-1.5" data-testid="admin-tab-link-taps">
              <MousePointerClick className="w-4 h-4" /> Links
            </TabsTrigger>
            <TabsTrigger value="stuck-payments" className="gap-1.5" data-testid="admin-tab-stuck-payments">
              <AlertOctagon className="w-4 h-4" /> Stuck Payments{stuckCount > 0 ? ` (${stuckCount})` : ''}
            </TabsTrigger>
            <TabsTrigger value="announcements" className="gap-1.5" data-testid="admin-tab-announcements">
              <Radio className="w-4 h-4" /> Announcements
            </TabsTrigger>
            <TabsTrigger value="accounts" className="gap-1.5" data-testid="admin-tab-accounts">
              <Ban className="w-4 h-4" /> Accounts
            </TabsTrigger>
          </TabsList>

          <TabsContent value="metrics" className="space-y-8 mt-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard icon={Users} label="Total Users" value={users.total} sub={`${users.verified} verified · ${users.signups_today} today`} testId="stat-total-users" />
          <StatCard icon={DollarSign} label="MRR (est.)" value={`$${revenue.mrr_estimate}`} sub={`${revenue.paying_users} paying · ${revenue.comped_users} promo/comp excluded`} testId="stat-mrr" />
          <StatCard icon={FileText} label="Invoices Created" value={invoices.total} sub={`${invoices.by_status.paid || 0} paid · ${invoices.by_status.sent || 0} sent`} testId="stat-invoices" />
          <StatCard icon={TrendingUp} label="Payment Volume" value={`$${payments.volume.toFixed(2)}`} sub={`${payments.completed_count} completed payments`} testId="stat-volume" />
          <StatCard icon={Percent} label="Platform Fees Collected" value={`$${payments.platform_fees_collected.toFixed(2)}`} sub="3.4% + 30¢ per invoice payment" testId="stat-fees" />
          <StatCard icon={Users} label="Campaigns" value={campaigns.total} sub={`${revenue.subscription_payments} subscription payments`} testId="stat-campaigns" />
          <StatCard icon={AlertTriangle} label="Client crashes (24h)" value={errors24h} sub={`${errorsTotal} recorded all-time`} testId="stat-client-errors" />
          <StatCard icon={AlertOctagon} label="Stuck Payments (24h+)" value={stuckCount} sub="Checkout created, invoice still unpaid" testId="stat-stuck-payments" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <DayChart title="Signups — last 30 days" data={users.signups_by_day} testId="chart-signups" />
          <DayChart title="Invoices created — last 30 days" data={invoices.created_by_day} testId="chart-invoices" />
        </div>

        <Card data-testid="plan-breakdown">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Plan Breakdown</CardTitle>
          </CardHeader>
          <CardContent className="flex gap-3 flex-wrap">
            <Badge variant="secondary">Free: {users.by_plan.free}</Badge>
            <Badge variant="secondary">Standard ($5): {users.by_plan.standard}{revenue.paying_by_plan ? ` (${revenue.paying_by_plan.standard} paying)` : ''}</Badge>
            <Badge variant="secondary">Pro ($8): {users.by_plan.pro}{revenue.paying_by_plan ? ` (${revenue.paying_by_plan.pro} paying)` : ''}</Badge>
            <Badge variant="secondary" data-testid="comped-users-badge">Comped (promo/referral/admin): {revenue.comped_users}</Badge>
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="inbox" className="mt-6">
            <InboxTab api={api} />
          </TabsContent>

          <TabsContent value="ambassadors" className="mt-6">
            <AmbassadorsTab api={api} />
          </TabsContent>

          <TabsContent value="comp-codes" className="mt-6">
            <Suspense fallback={<div className="text-sm text-muted-foreground p-4">Loading comp codes…</div>}>
              <CompCodesTab api={api} />
            </Suspense>
          </TabsContent>

          <TabsContent value="blog" className="mt-6">
            <Suspense fallback={<div className="text-sm text-muted-foreground p-4">Loading blog editor…</div>}>
              <BlogTab api={api} />
            </Suspense>
          </TabsContent>

          <TabsContent value="emails" className="mt-6">
            <Suspense fallback={<div className="text-sm text-muted-foreground p-4">Loading email previews…</div>}>
              <EmailPreviewTab api={api} />
            </Suspense>
          </TabsContent>

          <TabsContent value="link-taps" className="mt-6">
            <Suspense fallback={<div className="text-sm text-muted-foreground p-4">Loading link taps…</div>}>
              <LinkTapsTab api={api} />
            </Suspense>
          </TabsContent>

          <TabsContent value="stuck-payments" className="mt-6">
            <Suspense fallback={<div className="text-sm text-muted-foreground p-4">Loading stuck payments…</div>}>
              <StuckPaymentsTab api={api} />
            </Suspense>
          </TabsContent>

          <TabsContent value="announcements" className="mt-6">
            <Suspense fallback={<div className="text-sm text-muted-foreground p-4">Loading announcements…</div>}>
              <AnnouncementsTab api={api} />
            </Suspense>
          </TabsContent>

          <TabsContent value="accounts" className="mt-6">
            <Suspense fallback={<div className="text-sm text-muted-foreground p-4">Loading accounts…</div>}>
              <AccountsTab api={api} />
            </Suspense>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
